import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import jwt_decode from 'jwt-decode';
import { API_URL } from '../utils/apiConfig';
import { getApiErrorMessage } from '../utils/errorUtils';
import {
  SESSION_EXPIRED_MESSAGE,
  SESSION_INVALID_MESSAGE,
  formatFailedActionMessage,
  formatSuccessActionMessage,
} from '../utils/uiMessages';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoLogoutMinutes, setAutoLogoutMinutes] = useState(30);

  // Set auth token
  useEffect(() => {
    if (token) {
      // Format token with Bearer prefix for API requests
      const formattedToken = `Bearer ${token}`;
      axios.defaults.headers.common['Authorization'] = formattedToken;
      loadUser();
    } else {
      delete axios.defaults.headers.common['Authorization'];
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
    }
  }, [token]);

  // Check token expiration and validity
  useEffect(() => {
    if (token) {
      try {
        const decoded = jwt_decode(token);
        const currentTime = Date.now() / 1000;
        
        // Check if token is expired
        if (decoded.exp < currentTime) {
          logout();
          toast.error(SESSION_EXPIRED_MESSAGE);
          return;
        }
        
        // Check if token has the correct structure
        if (!decoded.id) {
          console.warn('Token has invalid structure, logging out');
          logout();
          toast.error(SESSION_INVALID_MESSAGE);
          return;
        }
        
      } catch (err) {
        console.error('Token validation failed:', err);
        logout();
        toast.error(SESSION_INVALID_MESSAGE);
      }
    }
  }, [token]);

  // Load user
  const loadUser = async () => {
    try {
      setLoading(true);
      
      // Use centralized API configuration
      const res = await axios.get(`${API_URL}/auth/me`);
      setUser(res.data.data);
      setIsAuthenticated(true);
      setError(null);

      // After user is loaded, try to load organization settings
      // to configure session timeout (auto logout) for this session.
      try {
        const orgRes = await axios.get(`${API_URL}/organizations/my`);
        const org = orgRes.data?.data;
        const orgAutoLogout = org?.settings?.autoLogout;
        if (typeof orgAutoLogout === 'number' && orgAutoLogout > 0) {
          setAutoLogoutMinutes(orgAutoLogout);
        } else {
          setAutoLogoutMinutes(30);
        }
      } catch (orgErr) {
        console.warn('Failed to load organization settings for auto logout:', orgErr);
        setAutoLogoutMinutes(30);
      }
    } catch (err) {
      console.error('Load user error:', err);
      setError(getApiErrorMessage(err, formatFailedActionMessage('load', 'user')));
      setToken(null);
      localStorage.removeItem('token');
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Clear all auth data and restart
  const clearAuthData = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
    localStorage.removeItem('token');
    sessionStorage.clear();
    delete axios.defaults.headers.common['Authorization'];
  };

  // Register user
  const register = async (userData) => {
    try {
      setLoading(true);
      const res = await axios.post(`${API_URL}/auth/register`, userData);
      // Store the raw token (without Bearer prefix)
      const rawToken = res.data.token;
      
      // Set token in localStorage and state
      setToken(rawToken);
      localStorage.setItem('token', rawToken);
      
      // Set axios header immediately
      const formattedToken = `Bearer ${rawToken}`;
      axios.defaults.headers.common['Authorization'] = formattedToken;
      
      // Now load user with proper headers
      await loadUser();
      toast.success('Registration successful');
      return res.data;
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.message || data?.errors?.[0]?.msg || 'Registration failed';
      setError(msg);
      toast.error(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Login user
  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      
      // Use centralized API configuration
      const res = await axios.post(`${API_URL}/auth/login`, { email, password });
      
      // Store the raw token (without Bearer prefix)
      const rawToken = res.data.token;
      
      // Set token in localStorage and state
      setToken(rawToken);
      localStorage.setItem('token', rawToken);
      
      // Set axios header immediately
      const formattedToken = `Bearer ${rawToken}`;
      axios.defaults.headers.common['Authorization'] = formattedToken;
      
      // Now load user with proper headers
      await loadUser();
      toast.success('Login successful');
      return res.data;
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = getApiErrorMessage(err, 'Login failed');
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout user
  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setIsAuthenticated(false);
    setUser(null);
    setError(null);
    delete axios.defaults.headers.common['Authorization'];
    toast.info('You have been logged out');
  };

  // Auto-logout on inactivity based on organization settings
  useEffect(() => {
    if (!isAuthenticated || !autoLogoutMinutes) {
      return;
    }

    let inactivityTimeout;

    const resetInactivityTimer = () => {
      if (!isAuthenticated || !autoLogoutMinutes) return;
      if (inactivityTimeout) {
        clearTimeout(inactivityTimeout);
      }
      inactivityTimeout = setTimeout(() => {
        toast.info('You have been logged out due to inactivity');
        logout();
      }, autoLogoutMinutes * 60 * 1000);
    };

    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

    activityEvents.forEach(event =>
      window.addEventListener(event, resetInactivityTimer)
    );

    // Start timer immediately when effect runs
    resetInactivityTimer();

    return () => {
      if (inactivityTimeout) {
        clearTimeout(inactivityTimeout);
      }
      activityEvents.forEach(event =>
        window.removeEventListener(event, resetInactivityTimer)
      );
    };
  }, [isAuthenticated, autoLogoutMinutes]);

  // Update user profile
  const updateProfile = async (userData) => {
    try {
      setLoading(true);
      const res = await axios.put(`${API_URL}/auth/updatedetails`, userData);
      setUser(res.data.data);
      toast.success(formatSuccessActionMessage('updated', 'Profile'));
      return res.data;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, formatFailedActionMessage('update', 'profile'));
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update password
  const updatePassword = async (passwordData) => {
    try {
      setLoading(true);
      const res = await axios.put(`${API_URL}/auth/updatepassword`, passwordData);
      toast.success(formatSuccessActionMessage('updated', 'Password'));
      return res.data;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, formatFailedActionMessage('update', 'password'));
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Forgot password
  const forgotPassword = async (email) => {
    try {
      setLoading(true);
      const res = await axios.post(`${API_URL}/auth/forgotpassword`, { email });
      toast.success(formatSuccessActionMessage('sent', 'Password reset email'));
      return res.data;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, formatFailedActionMessage('send', 'reset email'));
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Reset password
  const resetPassword = async (password, resetToken) => {
    try {
      setLoading(true);
      const res = await axios.put(`${API_URL}/auth/resetpassword/${resetToken}`, { password });
      toast.success(formatSuccessActionMessage('reset', 'Password'));
      return res.data;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, formatFailedActionMessage('reset', 'password'));
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        loading,
        error,
        register,
        login,
        logout,
        loadUser,
        updateProfile,
        updatePassword,
        forgotPassword,
        resetPassword,
        clearAuthData
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);